import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Crown, Check, X, BarChart3, MessageSquareReply, Lock, BadgeCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { userPlan, PLAN_INFO, PLAN_FEATURES } from "@/lib/plans";

const ROLE_NAME = { public_entity: "Ente Pubblico", business: "Impresa / P.IVA", citizen: "Privato", admin: "Portale" };
export const userIsPremium = (u) => userPlan(u) !== "free";

const Cell = ({ v }) => (v === true ? <Check className="w-4 h-4 text-emerald-600 mx-auto"/> : v === false ? <X className="w-4 h-4 text-slate-300 mx-auto"/> : <span>{v}</span>);
const KEYS = ["free", "standard", "professional"];

export const PlanCard = () => {
  const { user } = useAuth();
  const plan = userPlan(user);
  return (
    <div className="mt-8 card-premium p-6" data-testid="plan-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1"><Crown className="w-5 h-5 text-gold"/><h3 className="font-display text-xl font-bold">Il tuo piano</h3></div>
          <p className="text-sm text-slate-600 max-w-xl">{plan === "professional" ? "Piano Professional attivo: tutte le funzioni sono sbloccate." : plan === "standard" ? "Piano Standard attivo. Con Professional sblocchi video extra, priorità massima, newsletter e risposte alle recensioni." : "Piano Gratuito: le funzioni e i contenuti in eccesso restano oscurati e si riattivano con un abbonamento."}</p>
        </div>
        <div className="flex items-center gap-2"><span data-testid="plan-status" className={plan === "free" ? "chip-navy" : "chip-gold"}>{plan !== "free" && <BadgeCheck className="w-3.5 h-3.5"/>}{PLAN_INFO[plan].name}</span>
          <Link to="/piani" data-testid="plan-compare-link" className="btn-pill-gold text-xs">{plan === "professional" ? "Vedi i piani" : "Confronta e passa di piano"}</Link></div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500"><th className="py-2">Funzione</th>{KEYS.map((k) => <th key={k} className={`py-2 text-center ${plan === k ? "text-gold" : ""}`}>{PLAN_INFO[k].name}</th>)}</tr></thead>
          <tbody>{PLAN_FEATURES.map(([f, ...vals]) => <tr key={f} className="border-t border-slate-100"><td className="py-2 pr-3">{f}</td>{vals.map((v, i) => <td key={i} className={`py-2 text-center ${KEYS[i] === plan ? "font-semibold text-navy bg-gold/5" : "text-slate-600"}`}><Cell v={v}/></td>)}</tr>)}</tbody>
        </table>
      </div>
    </div>
  );
};

export const BioCard = () => {
  const { user, refresh } = useAuth();
  const [bio, setBio] = useState(user.bio || "");
  const save = async () => {
    try { await api.patch("/me", { bio }); toast.success("Descrizione salvata"); refresh(); }
    catch { toast.error("Errore nel salvataggio"); }
  };
  return (
    <div className="mt-8 card-premium p-6" data-testid="bio-card">
      <h3 className="font-display text-xl font-bold mb-1">Descrizione biografica</h3>
      <p className="text-sm text-slate-600 mb-3">{userIsPremium(user) ? "Visibile nella tua scheda profilo." : "Puoi scriverla ora: diventa visibile ai committenti con il profilo Premium."}</p>
      <textarea data-testid="bio-input" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={2000} rows={5} placeholder="Racconta chi sei, il tuo spettacolo, le esperienze principali…" className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm"/>
      <div className="flex items-center justify-between mt-2"><span className="text-xs text-slate-400">{bio.length}/2000</span><button data-testid="bio-save" onClick={save} className="btn-pill-gold text-sm">Salva</button></div>
    </div>
  );
};

const LABELS = { view: "Visualizzazioni", quote: "Preventivi calcolati", message: "Messaggi ricevuti", booking: "Prenotazioni" };

export const StatsCard = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/me/stats").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  return (
    <div className="mt-8 card-premium p-6" data-testid="stats-card">
      <div className="flex items-center gap-2 mb-4"><BarChart3 className="w-5 h-5 text-gold"/><h3 className="font-display text-xl font-bold">Statistiche profilo</h3></div>
      {s.locked ? (
        <p className="text-sm text-slate-600 flex items-center gap-2" data-testid="stats-locked"><Lock className="w-4 h-4 text-gold"/>Le statistiche su visualizzazioni e interazioni sono disponibili con il profilo Premium.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(LABELS).filter(([k]) => s[k]).map(([k, l]) => (
            <div key={k} className="rounded-xl bg-slate-50 p-4" data-testid={`stat-${k}`}>
              <div className="overline text-slate-500">{l}</div>
              <div className="font-display text-3xl font-bold mt-1">{s[k].last30}</div>
              <div className="text-xs text-slate-500">ultimi 30 giorni · {s[k].total} totali</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReplyBox = ({ review, onDone }) => {
  const [text, setText] = useState("");
  const send = async () => {
    try { await api.post(`/reviews/${review.id}/reply`, { text }); toast.success("Risposta pubblicata"); onDone(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="mt-2 flex gap-2">
      <input data-testid={`reply-input-${review.id}`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi una risposta pubblica…" className="flex-1 border border-slate-300 rounded-full px-4 py-2 text-sm"/>
      <button data-testid={`reply-send-${review.id}`} disabled={text.trim().length < 2} onClick={send} className="btn-pill-gold text-sm disabled:opacity-40">Rispondi</button>
    </div>
  );
};

export const MyReviewsCard = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const load = () => api.get(`/reviews/${user.id}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const premium = userPlan(user) === "professional";
  return (
    <div className="mt-8 card-premium p-6" data-testid="my-reviews-card">
      <div className="flex items-center gap-2 mb-4"><MessageSquareReply className="w-5 h-5 text-gold"/><h3 className="font-display text-xl font-bold">Le tue recensioni</h3></div>
      {items.length === 0 && <p className="text-sm text-slate-500">Nessuna recensione ricevuta.</p>}
      {!premium && items.length > 0 && <p className="text-xs text-slate-500 mb-3 flex items-center gap-1" data-testid="reply-locked"><Lock className="w-3.5 h-3.5 text-gold"/>Puoi leggere le recensioni; per rispondere serve il piano Professional.</p>}
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className="border border-slate-200 rounded-xl p-4" data-testid={`my-review-${r.id}`}>
            <div className="text-gold font-semibold text-sm">{r.rating}/5 · <span className="text-slate-500 font-normal">{ROLE_NAME[r.fromRole] || r.fromRole}</span></div>
            <p className="text-sm text-slate-700 mt-1">{r.comment || "—"}</p>
            {r.reply ? <p className="mt-2 text-sm border-l-2 border-gold pl-3 text-slate-600">{r.reply.text}</p> : premium && <ReplyBox review={r} onDone={load}/>}
          </div>
        ))}
      </div>
    </div>
  );
};
